/**
 * Static API Explorer sidebar entries that are not yet (or not always) present
 * in RBAC permission_nodes_um. Merged client-side; skipped when RBAC already
 * grants the same queryParams.api values.
 */

// Set to true to re-enable the client-side "Bulk Payout" sidebar menu injection.
const ENABLE_BULK_PAYOUT_MENU = false;

const BULK_PAYOUT_PARENT_ID = 900100;

const BULK_PAYOUT_LEAVES: ReadonlyArray<{
  nodeId: number;
  nodeName: string;
  api: string;
  position: number;
}> = [
  { nodeId: 900101, nodeName: 'Download Bulk Payout Template', api: 'payout-batch-template', position: 1 },
  { nodeId: 900102, nodeName: 'Upload Bulk Payout File', api: 'payout-batch-upload', position: 2 },
  { nodeId: 900103, nodeName: 'Confirm Bulk Payout Batch', api: 'payout-batch-confirm', position: 3 },
  { nodeId: 900104, nodeName: 'List Bulk Payout Batches', api: 'payout-batch-list', position: 4 },
  { nodeId: 900105, nodeName: 'Get Bulk Payout Batch Detail', api: 'payout-batch-detail', position: 5 },
  { nodeId: 900106, nodeName: 'Update Bulk Payout Batch Row', api: 'payout-batch-row-update', position: 6 },
  { nodeId: 900107, nodeName: 'Delete Bulk Payout Batch Row', api: 'payout-batch-row-delete', position: 7 },
  { nodeId: 900108, nodeName: 'Delete Bulk Payout Batch', api: 'payout-batch-delete', position: 8 },
];

function resolveApiKey(item: any): string | undefined {
  const qp = item?.nodeInfo?.queryParams;
  if (!qp) return undefined;
  if (typeof qp === 'object') return qp.api;
  if (typeof qp === 'string' && qp.trim() !== '') {
    try {
      return new Function('return (' + qp + ')')()?.api;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Merge Bulk Payout sidebar nodes into an api-explorer menu list (idempotent). */
export function mergeApiExplorerMenu(items: any[] | null | undefined): any[] {
  const list: any[] = Array.isArray(items) ? items.map(i => ({ ...i, nodeInfo: { ...i?.nodeInfo } })) : [];

  if (!ENABLE_BULK_PAYOUT_MENU) {
    // Strip out any previously-cached Bulk Payout nodes (e.g. from localStorage)
    // and skip injecting new ones.
    const bulkNodeIds = new Set<number>([BULK_PAYOUT_PARENT_ID, ...BULK_PAYOUT_LEAVES.map(l => l.nodeId)]);
    return list.filter(item => !bulkNodeIds.has(item.nodeId));
  }

  const existingApis = new Set(list.map(resolveApiKey).filter(Boolean) as string[]);
  const bulkApis = BULK_PAYOUT_LEAVES.map(l => l.api);

  if (bulkApis.every(api => existingApis.has(api))) {
    return list;
  }

  const rootItems = list.filter(item => {
    const parentId = item.parentId;
    return parentId == null || parentId === 0 || !list.some(p => p.nodeId === parentId);
  });
  const maxRootPos = rootItems.reduce(
    (max, item) => Math.max(max, Number(item.nodeInfo?.position ?? 0)),
    0
  );

  if (!list.some(i => i.nodeId === BULK_PAYOUT_PARENT_ID)) {
    list.push({
      nodeId: BULK_PAYOUT_PARENT_ID,
      parentId: 0,
      nodeName: 'Bulk Payout',
      nodeInfo: {
        type: 'menu',
        category: 'api-explorer',
        position: maxRootPos + 1
      }
    });
  }

  for (const leaf of BULK_PAYOUT_LEAVES) {
    if (existingApis.has(leaf.api)) continue;
    list.push({
      nodeId: leaf.nodeId,
      parentId: BULK_PAYOUT_PARENT_ID,
      nodeName: leaf.nodeName,
      nodeInfo: {
        type: 'sub-menu',
        category: 'api-explorer',
        menuLink: '/api-explorer',
        queryParams: { api: leaf.api },
        position: leaf.position
      }
    });
  }

  return list;
}
